import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Activity, Plus, Server as ServerIcon, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { serversApi, type Server } from '../api.js';
import { ServerFormModal } from '../components/ServerFormModal.js';

export function ServersDashboard() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<Server | undefined>();
  const qc = useQueryClient();

  const serversQuery = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list(),
  });

  // Health check em batch (fan-out paralelo)
  const servers = serversQuery.data ?? [];
  const healthQueries = useQueries({
    queries: servers.map((s) => ({
      queryKey: ['server-health', s.id],
      queryFn: () => serversApi.health(s.id),
      retry: false,
      staleTime: 30_000,
    })),
  });

  const handleDelete = async (s: Server): Promise<void> => {
    if (!window.confirm(`Apagar servidor "${s.name}"? Esta ação é irreversível.`)) return;
    try {
      await serversApi.delete(s.id);
      toast.success('Servidor removido');
      void qc.invalidateQueries({ queryKey: ['servers'] });
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-n-900">Servidores</h1>
          <p className="text-[13px] text-n-500">
            Gerencie servidores ChatPlus e suas instâncias (tenants)
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingServer(undefined);
            setModalOpen(true);
          }}
        >
          <Plus size={14} /> Adicionar Servidor
        </Button>
      </div>

      {serversQuery.isLoading && (
        <div className="py-12 text-center text-[13px] text-n-500">Carregando...</div>
      )}

      {serversQuery.data?.length === 0 && (
        <Card className="p-12 text-center">
          <ServerIcon size={48} className="mx-auto mb-3 text-n-300" />
          <h3 className="text-[16px] font-semibold text-n-700">Nenhum servidor cadastrado</h3>
          <p className="mt-1 text-[13px] text-n-500">
            Adicione seu primeiro servidor ChatPlus pra começar a gerenciar tenants.
          </p>
          <Button
            className="mt-4"
            onClick={() => {
              setEditingServer(undefined);
              setModalOpen(true);
            }}
          >
            <Plus size={14} /> Adicionar Servidor
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {servers.map((s, idx) => {
          const health = healthQueries[idx];
          const isUp = health?.data?.status === 200;
          const isChecking = health?.isFetching;
          const snapshot = health?.data ?? s.last_health_snapshot;

          return (
            <Card key={s.id} className="overflow-hidden transition-shadow hover:shadow-md">
              <Link
                to="/servidores/$id"
                params={{ id: String(s.id) }}
                className="block px-5 py-4"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2.5">
                    <div className="flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                      <ServerIcon size={18} strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-semibold text-n-900">{s.name}</div>
                      <div className="truncate text-[11px] text-n-500 font-mono">{s.url}</div>
                    </div>
                  </div>
                  <span
                    className={`flex-shrink-0 whitespace-nowrap rounded-pill px-2 py-0.5 text-[10px] font-semibold ${
                      isUp
                        ? 'bg-success-50 text-success-700'
                        : isChecking
                          ? 'bg-n-100 text-n-600'
                          : 'bg-danger-50 text-danger-700'
                    }`}
                  >
                    {isChecking ? 'CHECANDO' : isUp ? 'ONLINE' : 'OFFLINE'}
                  </span>
                </div>

                {snapshot && 'counters' in snapshot && snapshot.counters && (
                  <div className="flex gap-4 border-t border-n-100 pt-3">
                    <div className="flex items-center gap-1.5 text-[12px] text-n-600">
                      <Users size={12} />
                      <span>
                        <strong className="text-n-900">{snapshot.counters.tenants.total}</strong>{' '}
                        tenants
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[12px] text-n-600">
                      <Activity size={12} />
                      <span>
                        <strong className="text-n-900">{snapshot.counters.users_active}</strong>{' '}
                        users
                      </span>
                    </div>
                  </div>
                )}
              </Link>

              <div className="flex items-center justify-end gap-1 border-t border-n-100 bg-n-50 px-2 py-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingServer(s);
                    setModalOpen(true);
                  }}
                >
                  Editar
                </Button>
                <button
                  type="button"
                  onClick={() => void handleDelete(s)}
                  className="rounded-md p-1.5 text-n-500 hover:bg-danger-50 hover:text-danger-600"
                  title="Apagar"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      <ServerFormModal
        // key força re-mount quando troca de servidor — sem isso o useState
        // dos campos congela com os valores iniciais do PRIMEIRO render
        // (lição #87 do CLAUDE.md sobre useState(initial) congelado).
        key={editingServer?.id ?? 'new'}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingServer(undefined);
        }}
        server={editingServer}
      />
    </div>
  );
}
